import * as React from 'react';
import {createPortal} from 'react-dom';
import clsx from 'clsx';

import {useNavManagerWithFallback} from 'common/contexts/react/nav-manager';
import {SpinnerReact} from 'common/components/spinner';
import {SpinnerColor} from 'common/components/spinner/types';
import {SvgIconReact} from 'common/components/svg-icon';
import type {SvgIconType} from 'common/components/svg-icon/types';
import {TooltipReact} from 'common/components/tooltip';
import type {TooltipProps} from 'common/components/tooltip/component';
import type {TooltipAttrs, TooltipJsxProps} from 'common/components/tooltip/types';
import {flattenAttrsOn} from 'common/util/flatten-attrs-on';

import {BUTTON_ROOT_CLASS, ButtonLayout, ButtonSize, ButtonTheme, ButtonType} from './constants';
import {layerRootRules} from './layer-root-rules';
import {scopeStyleOverride} from './style-override';
import type {ButtonProps} from './types';
import css from './index.styl?inline';

const BUTTON_STYLES_ID = `mp-button-react-styles`;
const BUTTON_CSS = layerRootRules(css, `.${BUTTON_ROOT_CLASS}`);

/**
 * The button CSS goes once into each document or shadow root that contains a button. Its root rules
 * sit in a cascade layer, so the caller's rules on the button win, and it is prepended so the
 * containing root's own rules also win ties against the rest.
 */
function attachButtonStyles(rootElement: HTMLElement) {
  const rootNode = rootElement.getRootNode();
  const styleRoot = rootNode instanceof ShadowRoot ? rootNode : document.head;
  const existingStyles =
    rootNode instanceof ShadowRoot
      ? rootNode.getElementById(BUTTON_STYLES_ID)
      : document.getElementById(BUTTON_STYLES_ID);
  if (existingStyles) {
    return;
  }
  const buttonStyles = document.createElement(`style`);
  buttonStyles.id = BUTTON_STYLES_ID;
  buttonStyles.textContent = BUTTON_CSS;
  styleRoot.prepend(buttonStyles);
}

/** Where tooltips are portaled so a clipping or stacking ancestor of the button can't hide them. */
function getTooltipPortalTarget(): Element {
  return (
    document.querySelector(`#width_wrapper`) ??
    document.querySelector(`.width_wrapper`) ??
    document.querySelector(`mp-browser-context-root`) ??
    document.body
  );
}

function ButtonTooltip({tooltip, rootElement}: {tooltip: TooltipAttrs; rootElement: HTMLElement}) {
  const tooltipProps = {
    ...flattenAttrsOn<TooltipJsxProps>({attrs: {'delay-in': 10, 'delay-out': 10, ...tooltip}}),
    text: tooltip.text,
    parentEl: rootElement,
  } as TooltipProps;
  return createPortal(<TooltipReact {...tooltipProps} />, getTooltipPortalTarget());
}

/**
 * Identifies a button for its scoped `styleOverride`. A module counter rather than `React.useId`, so
 * rendering a button doesn't shift the ids `useId` hands to everything after it in the tree.
 */
let buttonInstanceCounter = 0;
function getButtonInstanceId(): string {
  buttonInstanceCounter += 1;
  return `button-${buttonInstanceCounter}`;
}

function getTextContent(children: React.ReactNode): string | null {
  const childList = React.Children.toArray(children);
  if (childList.some((child) => typeof child !== `string` && typeof child !== `number`)) {
    return null;
  }
  return childList.join(``).trim();
}

export const ButtonReact = React.forwardRef<HTMLElement, ButtonProps>(function ButtonComponent(
  {
    children,
    className,
    class: classAttribute,
    style,
    onClick,
    onKeyDown,
    disabled = false,
    notFocusable = false,
    readonly = false,
    styleOverride,
    elref,
    highlightIcon = false,
    fillIcon = false,
    icon,
    iconColor,
    iconRight,
    size = ButtonSize.Medium,
    theme = ButtonTheme.Secondary,
    type = ButtonType.Info,
    pressed = false,
    tooltip,
    label,
    layout = ButtonLayout.Horizontal,
    disablePressedStyles = false,
    disabledStaysOpaque = false,
    href,
    download,
    target,
    role = `button`,
    tabIndex,
    'aria-label': ariaLabel,
    ...rest
  },
  forwardedRef,
) {
  const navManager = useNavManagerWithFallback();
  const [rootElement, setRootElement] = React.useState<HTMLElement | null>(null);
  const anchorRef = React.useRef<HTMLAnchorElement>(null);
  const [instanceId] = React.useState(getButtonInstanceId);

  const attachRoot = React.useCallback(
    (element: HTMLElement | null) => {
      setRootElement(element);
      if (typeof forwardedRef === `function`) {
        forwardedRef(element);
      } else if (forwardedRef) {
        forwardedRef.current = element;
      }
    },
    [forwardedRef],
  );

  React.useLayoutEffect(() => {
    if (rootElement) {
      attachButtonStyles(rootElement);
    }
  }, [rootElement]);

  // The native listeners attach once per element and read the latest props from here.
  const latestProps = React.useRef({onClick, disabled, href, download, target, navManager});
  React.useLayoutEffect(() => {
    latestProps.current = {onClick, disabled, href, download, target, navManager};
  });

  React.useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!rootElement || !anchor) {
      return undefined;
    }
    // Capturing on the root runs ahead of every other handler for a click anywhere in the button,
    // so a disabled button swallows the click the way a native disabled button does.
    const handleDisabledClick = (event: MouseEvent) => {
      if (latestProps.current.disabled) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    const handleAnchorClick = (event: MouseEvent) => {
      const {href: currentHref, download: currentDownload, target: currentTarget} = latestProps.current;
      if (currentHref && !currentDownload) {
        latestProps.current.navManager.navigate(currentHref, {
          triggerEvent: event,
          fromAnchorLink: true,
          anchorTarget: currentTarget ?? ``,
        });
      }
    };
    const handleClick = (event: MouseEvent) => latestProps.current.onClick?.(event);
    rootElement.addEventListener(`click`, handleDisabledClick, {capture: true});
    anchor.addEventListener(`click`, handleAnchorClick);
    rootElement.addEventListener(`click`, handleClick);
    return () => {
      rootElement.removeEventListener(`click`, handleDisabledClick, {capture: true});
      anchor.removeEventListener(`click`, handleAnchorClick);
      rootElement.removeEventListener(`click`, handleClick);
    };
  }, [rootElement]);

  /** Enter and Space activate the focused root the way they activate a native button. */
  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented || event.target !== rootElement || disabled || notFocusable) {
      return;
    }
    if (event.key === `Enter` || event.key === ` `) {
      event.preventDefault();
      // A click on the root never reaches the anchor's click handler, so `href` buttons click the
      // anchor itself to navigate.
      (href ? anchorRef.current : rootElement)?.click();
    }
  };

  const textContent = getTextContent(children);
  const hasContent = textContent === null || textContent.length > 0;
  const isIconOnly = (!hasContent && Boolean(icon)) || textContent?.length === 1;
  const isDestructive = type === ButtonType.Destructive || icon === `trashcan`;
  const tooltipConfig = tooltip?.text ? tooltip : label ? {text: label} : null;
  const instanceSelector = `[data-button-instance="${instanceId}"]`;
  const iconStyle = isIconOnly
    ? {margin: 0}
    : {
        marginLeft: `var(--button-icon-margin-left, 0px)`,
        marginRight: `var(--button-icon-margin-right, 8px)`,
      };
  const spinnerColor = theme === ButtonTheme.Primary ? SpinnerColor.White : SpinnerColor.Blue;

  // React 18 sets every prop on a custom element as an attribute, so `class` stands in for
  // `className` and false booleans are left out rather than written as "false".
  return React.createElement(
    `mp-button-root`,
    {
      ...rest,
      ref: attachRoot,
      class: clsx(BUTTON_ROOT_CLASS, className, classAttribute),
      style,
      role,
      tabIndex: tabIndex ?? (disabled || notFocusable ? undefined : 0),
      'aria-label': label || ariaLabel || (isIconOnly && icon) || undefined,
      'aria-disabled': disabled || undefined,
      disabled: disabled || undefined,
      elref,
      'data-button-theme': theme,
      'data-button-size': size,
      'data-button-layout': layout,
      'data-button-icon': icon || undefined,
      'data-button-pressed': pressed || undefined,
      'data-button-disabled': disabled || undefined,
      'data-button-readonly': readonly || undefined,
      'data-button-instance': styleOverride ? instanceId : undefined,
      onKeyDown: handleKeyDown,
    },
    // The override's root rules join the button's root layer, so they rank against the size and theme
    // rules by specificity, as `:host` rules did side by side in the Panel Button's shadow root.
    styleOverride && rootElement ? (
      <style>
        {layerRootRules(
          scopeStyleOverride(styleOverride, instanceSelector, rootElement.getRootNode() instanceof ShadowRoot),
          instanceSelector,
        )}
      </style>
    ) : null,
    // The root takes focus, so a link's anchor stays out of the Tab order. Without `href` the anchor
    // can't take focus at all, and a tabindex would let a click focus it instead of the root.
    <a
      ref={anchorRef}
      elref='buttonContainer'
      tabIndex={href ? -1 : undefined}
      className={clsx(`mp-button-container`, `mp-button-${theme}`, `mp-button-layout-${layout}`, `type-${type}`, {
        'type-destructive': isDestructive,
        'is-icon-only': isIconOnly,
        'is-pressed': pressed,
        'is-disabled': disabled,
        'disable-pressed-styles': disablePressedStyles,
        'disabled-stays-opaque': disabledStaysOpaque,
      })}
      href={href}
      target={target}
      download={download}
    >
      <div className='mp-button-animation-layer' />
      <div className='mp-button-content'>
        {icon === `spinner` ? (
          <SpinnerReact color={spinnerColor} style={iconStyle} />
        ) : icon ? (
          <SvgIconReact
            icon={icon as SvgIconType}
            color={iconColor}
            className={clsx({highlighted: highlightIcon, filled: fillIcon})}
          />
        ) : null}
        {hasContent ? <span className='mp-button-content-text'>{children}</span> : null}
        {iconRight === `spinner` ? (
          <SpinnerReact color={spinnerColor} style={iconStyle} />
        ) : iconRight ? (
          <SvgIconReact icon={iconRight as SvgIconType} className='right' />
        ) : null}
      </div>
      {tooltipConfig && !pressed && rootElement ? (
        <ButtonTooltip tooltip={tooltipConfig} rootElement={rootElement} />
      ) : null}
    </a>,
  );
});
